import { usePrices } from '@api/usePrices'
import { useLocalStorage } from '@gravitate-js/excalibrr'
import { Form, type FormInstance } from 'antd'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useUserSettings } from '@/hooks/settings'
import dayjs from 'dayjs'
import type { SupplyDirectoryGridQueryFilters } from '../SupplyDirectory/types'
import { getInitialQueryFilters } from '../SupplyDirectory/util'
import type { EventScheduleForm, OutageForm, RecurringScheduleForm } from '../components/EditOutageDrawer/types'
import { formatFormToOutage, initializeFormValues, isStartEndInvalid } from '../components/EditOutageDrawer/util'
import type { SupplyDirectoryHeaderFilters } from '../types'

export type SupplyManagementContextType = {
  supplyDirectoryData: unknown
  isSupplyDirectoryFetching: boolean
  isSupplyDirectoryRefetching: boolean
  drawerData: Gravitate.SupplyOutage<Gravitate.EventSchedule | Gravitate.RecurringSchedule> | null
  supplyOutageMetadata: unknown
  outageForm: FormInstance<OutageForm<EventScheduleForm | RecurringScheduleForm>>
  isDrawerOpen: boolean
  errorMessage: string | null
  headerFilters: SupplyDirectoryHeaderFilters
  supplyDirectoryGridFilters: SupplyDirectoryGridQueryFilters
  setSupplyDirectoryGridFilters: React.Dispatch<React.SetStateAction<SupplyDirectoryGridQueryFilters>>
  handleUpdateHeaderFilters: (value: SupplyDirectoryHeaderFilters) => void
  openEditOutage: (outageData?: Gravitate.SupplyOutage, overrideIsForever?: boolean) => void
  closeEditOutage: () => void
  cancelOutage: (outageId: string) => Promise<void>
  upsertOutage: () => void
}

const SupplyManagementContext = createContext<SupplyManagementContextType | undefined>(undefined)

type SupplyManagementProviderProps = {
  children: React.ReactNode
}

const defaultHeaderFilters = {
  priceLimit: 5,
  groupBy: 'terminal',
  includePlannedVolume: false,
  dateControl: 'Now' as const,
}

const HEADER_FILTERS_KEY = `supply-management::headerFilters`

export function SupplyManagementProvider({ children }: SupplyManagementProviderProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerData, setDrawerData] = useState<Gravitate.SupplyOutage | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [outageForm] = Form.useForm<OutageForm>()
  const { upsertSupplyOutageMutation, cancelSupplyOutageMutation, usePriceOverviewQuery, supplyOutageMetadata } =
    usePrices()
  const { mutateAsync: upsertAsync } = upsertSupplyOutageMutation()
  const { mutateAsync: cancelAsync } = cancelSupplyOutageMutation()
  const location = useLocation()
  const { updateSetting, getSetting } = useUserSettings()

  const initialFilters = { ...defaultHeaderFilters, filter: getInitialQueryFilters(location, 'Now') }

  const { value: _headerFilters, setValue: setHeaderFiltersAsync } = useLocalStorage<SupplyDirectoryHeaderFilters>(
    HEADER_FILTERS_KEY,
    initialFilters
  )
  const [supplyDirectoryGridFilters, setSupplyDirectoryGridFilters] = useState<SupplyDirectoryGridQueryFilters>({})

  const headerFilters = _headerFilters ? _headerFilters : initialFilters
  useEffect(() => {
    getSetting(HEADER_FILTERS_KEY).then((filter) => {
      if (filter) {
        setHeaderFiltersAsync(filter)
      }
    })
  }, [])

  const {
    data: { data: supplyDirectoryData } = {},
    isLoading: isSupplyDirectoryFetching,
    isRefetching: isSupplyDirectoryRefetching,
  } = usePriceOverviewQuery(
    _headerFilters
      ? {
          ...headerFilters,
          filter: { ...headerFilters.filter, ...supplyDirectoryGridFilters },
        }
      : initialFilters
  )

  function handleUpdateHeaderFilters(value: SupplyDirectoryHeaderFilters) {
    setHeaderFiltersAsync(value)
    updateSetting(HEADER_FILTERS_KEY, value)
  }

  function openEditOutage(outageData?: Gravitate.SupplyOutage, overrideIsForever?: boolean) {
    outageForm.resetFields()
    setDrawerData(outageData)
    if (outageData) {
      const formData = initializeFormValues(outageData, overrideIsForever)
      outageForm.setFieldsValue(formData)
    }

    setIsDrawerOpen(true)
  }

  function closeEditOutage() {
    setIsDrawerOpen(false)
    outageForm.resetFields()
    setErrorMessage(null)
  }

  async function cancelOutage(outageId: string) {
    closeEditOutage()
    cancelAsync(outageId)
  }

  function upsertOutage() {
    const outage = formatFormToOutage(outageForm.getFieldsValue(), drawerData?.id)
    if (isStartEndInvalid(outage.schedule_local.start_date, outage.schedule_local.end_date)) {
      setErrorMessage('End date must be after start date')
      return
    }
    closeEditOutage()
    upsertAsync({ outage, as_of_utc: headerFilters?.filter.as_of || dayjs.utc().toISOString() })
  }

  const contextValue = useMemo(
    () => ({
      supplyDirectoryData,
      isSupplyDirectoryFetching,
      isSupplyDirectoryRefetching,
      drawerData,
      supplyOutageMetadata,
      outageForm,
      isDrawerOpen,
      errorMessage,
      headerFilters,
      supplyDirectoryGridFilters,
      setSupplyDirectoryGridFilters,
      handleUpdateHeaderFilters,
      openEditOutage,
      closeEditOutage,
      cancelOutage,
      upsertOutage,
    }),
    /** ADD ANY NEW STATE TO THIS DEPENDENCIES ARRAY */
    [
      supplyDirectoryData,
      isSupplyDirectoryFetching,
      isSupplyDirectoryRefetching,
      drawerData,
      supplyOutageMetadata,
      outageForm,
      isDrawerOpen,
      errorMessage,
      headerFilters,
    ]
  )

  return <SupplyManagementContext.Provider value={contextValue}>{children}</SupplyManagementContext.Provider>
}

export function useSupplyManagementContext() {
  const context = useContext(SupplyManagementContext)
  if (context === undefined) {
    throw new Error('useSupplyManagement must be used within an SupplyManagementProvider')
  }
  return context
}
